// TimeSlotComponent.jsx - Fixed to prevent infinite state update loops
import React, { useState, useEffect, useRef } from "react";
import {
  Grid,
  FormControlLabel,
  Checkbox,
  Select,
  MenuItem,
  IconButton,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Divider,
  FormGroup,
  Tooltip,
  Snackbar,
  Alert,
} from "@mui/material";

import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
  Check as CheckIcon,
} from "@mui/icons-material";

// Full array of time slots in 15-minute increments
const ALL_TIME_SLOTS = [
  "12:00 am",
  "12:15 am",
  "12:30 am",
  "12:45 am",
  "01:00 am",
  "01:15 am",
  "01:30 am",
  "01:45 am",
  "02:00 am",
  "02:15 am",
  "02:30 am",
  "02:45 am",
  "03:00 am",
  "03:15 am",
  "03:30 am",
  "03:45 am",
  "04:00 am",
  "04:15 am",
  "04:30 am",
  "04:45 am",
  "05:00 am",
  "05:15 am",
  "05:30 am",
  "05:45 am",
  "06:00 am",
  "06:15 am",
  "06:30 am",
  "06:45 am",
  "07:00 am",
  "07:15 am",
  "07:30 am",
  "07:45 am",
  "08:00 am",
  "08:15 am",
  "08:30 am",
  "08:45 am",
  "09:00 am",
  "09:15 am",
  "09:30 am",
  "09:45 am",
  "10:00 am",
  "10:15 am",
  "10:30 am",
  "10:45 am",
  "11:00 am",
  "11:15 am",
  "11:30 am",
  "11:45 am",
  "12:00 pm",
  "12:15 pm",
  "12:30 pm",
  "12:45 pm",
  "01:00 pm",
  "01:15 pm",
  "01:30 pm",
  "01:45 pm",
  "02:00 pm",
  "02:15 pm",
  "02:30 pm",
  "02:45 pm",
  "03:00 pm",
  "03:15 pm",
  "03:30 pm",
  "03:45 pm",
  "04:00 pm",
  "04:15 pm",
  "04:30 pm",
  "04:45 pm",
  "05:00 pm",
  "05:15 pm",
  "05:30 pm",
  "05:45 pm",
  "06:00 pm",
  "06:15 pm",
  "06:30 pm",
  "06:45 pm",
  "07:00 pm",
  "07:15 pm",
  "07:30 pm",
  "07:45 pm",
  "08:00 pm",
  "08:15 pm",
  "08:30 pm",
  "08:45 pm",
  "09:00 pm",
  "09:15 pm",
  "09:30 pm",
  "09:45 pm",
  "10:00 pm",
  "10:15 pm",
  "10:30 pm",
  "10:45 pm",
  "11:00 pm",
  "11:15 pm",
  "11:30 pm",
  "11:45 pm",
];

const TimeSlotComponent = ({
  label,
  isAvailable,
  setIsAvailable,
  dayTimeSlots = [],
  currentDay,
  allDays,
  setDayTimeSlots,
}) => {
  // Track state updates
  const skipUpdate = useRef(false);
  const initialMount = useRef(true);

  // States
  const [checked, setChecked] = useState(isAvailable);
  const defaultSlot = { timeStart: "", timeEnd: "" };

  // Time slots state - initialize properly
  const [slots, setSlots] = useState(() => {
    return dayTimeSlots.length > 0 ? [...dayTimeSlots] : [{ ...defaultSlot }];
  });

  // Dialog states
  const [copyDialogOpen, setCopyDialogOpen] = useState(false);
  const [selectedDays, setSelectedDays] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [feedback, setFeedback] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Other days for copying
  const otherDays = allDays ? allDays.filter((day) => day !== currentDay) : [];

  // Handle initial mount
  useEffect(() => {
    if (initialMount.current) {
      initialMount.current = false;
      return;
    }
  }, []);

  // Only sync checked state with props when props change
  useEffect(() => {
    if (checked !== isAvailable) {
      setChecked(isAvailable);
    }
  }, [isAvailable]);

  // Only sync slots with props when dayTimeSlots changes externally
  useEffect(() => {
    if (skipUpdate.current) {
      skipUpdate.current = false;
      return;
    }

    // Only update slots if dayTimeSlots has content
    if (isAvailable && dayTimeSlots && Array.isArray(dayTimeSlots)) {
      if (dayTimeSlots.length > 0) {
        setSlots([...dayTimeSlots]);
      } else if (slots.length === 0) {
        setSlots([{ ...defaultSlot }]);
      }
    }
  }, [dayTimeSlots, isAvailable]);

  // Handle checkbox change
  const handleSwitchChange = (event) => {
    const isChecked = event.target.checked;

    // Prevent loops by setting a flag
    skipUpdate.current = true;
    setChecked(isChecked);

    if (isChecked) {
      // Use existing slots or initialize with empty
      if (slots.length === 0) {
        setSlots([{ ...defaultSlot }]);
      }

      // Only send valid slots to parent
      const validSlots = slots.filter((slot) => slot.timeStart && slot.timeEnd);
      setIsAvailable(validSlots, true);
    } else {
      setIsAvailable([], false);
    }
  };

  // Handle changing a time slot value
  const handleSlotChange = (index, field, value) => {
    // Set flag to prevent loops
    skipUpdate.current = true;

    // Update the slot
    const newSlots = [...slots];
    if (index >= 0 && index < newSlots.length) {
      newSlots[index] = { ...newSlots[index], [field]: value };
      setSlots(newSlots);
    }

    // Notify parent with valid slots only
    const validSlots = newSlots.filter(
      (slot) => slot.timeStart && slot.timeEnd
    );
    if (checked) {
      setIsAvailable(validSlots, true);
    }
  };

  // Handle adding a new time slot
  const addSlot = () => {
    // Set flag to prevent loops
    skipUpdate.current = true;

    // Add new slot
    setSlots((prevSlots) => [...prevSlots, { ...defaultSlot }]);

    // No need to notify parent yet - the new slot is empty
  };

  // Handle removing a time slot
  const removeSlot = (index) => {
    if (slots.length <= 1) return;

    // Set flag to prevent loops
    skipUpdate.current = true;

    // Remove the slot
    const newSlots = slots.filter((_, i) => i !== index);
    setSlots(newSlots);

    // Notify parent with valid slots only
    const validSlots = newSlots.filter(
      (slot) => slot.timeStart && slot.timeEnd
    );
    if (checked) {
      setIsAvailable(validSlots, true);
    }
  };

  // Dialog handlers
  const handleOpenCopyDialog = () => {
    setCopyDialogOpen(true);
  };

  const handleCloseCopyDialog = () => {
    setCopyDialogOpen(false);
    setSelectedDays([]);
    setSelectAll(false);
  };

  const handleSelectDay = (day) => {
    setSelectedDays((prev) => {
      if (prev.includes(day)) {
        return prev.filter((d) => d !== day);
      }
      return [...prev, day];
    });
  };

  const handleSelectAllDays = (event) => {
    const isChecked = event.target.checked;
    setSelectAll(isChecked);
    setSelectedDays(isChecked ? [...otherDays] : []);
  };

  // Handle copying time slots
  const handleCopyTimeSlots = () => {
    if (!setDayTimeSlots || typeof setDayTimeSlots !== "function") {
      setFeedback({
        open: true,
        message: "Unable to copy time slots. Please try again.",
        severity: "error",
      });
      return;
    }

    try {
      // Get only valid slots
      const validSlots = slots.filter((slot) => slot.timeStart && slot.timeEnd);

      // Validate
      if (validSlots.length === 0) {
        setFeedback({
          open: true,
          message: "Please set at least one complete time slot before copying",
          severity: "warning",
        });
        return;
      }

      // Copy to each selected day
      selectedDays.forEach((day) => {
        // Create deep copies
        const slotsCopy = validSlots.map((slot) => ({
          timeStart: slot.timeStart,
          timeEnd: slot.timeEnd,
        }));

        // Call parent function
        setDayTimeSlots(day, slotsCopy, true);
      });

      // Show feedback
      setFeedback({
        open: true,
        message: `Successfully copied ${validSlots.length} time slot(s) to ${selectedDays.length} day(s)`,
        severity: "success",
      });

      // Close dialog
      handleCloseCopyDialog();
    } catch (error) {
      console.error("Error copying time slots:", error);
      setFeedback({
        open: true,
        message:
          "Error copying time slots: " + (error.message || "Unknown error"),
        severity: "error",
      });
    }
  };

  // If not checked, just show the checkbox
  if (!checked) {
    return (
      <Box sx={{ mb: 2 }}>
        <Grid container spacing={2} alignItems="center" sx={{ mb: 1 }}>
          <Grid item md={2} xs={12}>
            <FormControlLabel
              control={
                <Checkbox checked={checked} onChange={handleSwitchChange} />
              }
              label={label}
            />
          </Grid>
        </Grid>
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 2 }}>
      {/* First row with day label */}
      <Grid container spacing={2} alignItems="center" sx={{ mb: 1 }}>
        <Grid item md={2} xs={12}>
          <FormControlLabel
            control={
              <Checkbox checked={checked} onChange={handleSwitchChange} />
            }
            label={label}
          />
        </Grid>

        <Grid item md={4} xs={12}>
          <Select
            value={slots[0]?.timeStart || ""}
            onChange={(e) => handleSlotChange(0, "timeStart", e.target.value)}
            fullWidth
            size="small"
            sx={{ height: "40px" }}
            displayEmpty
          >
            <MenuItem value="" disabled>
              Select start time
            </MenuItem>
            {ALL_TIME_SLOTS.map((time) => (
              <MenuItem key={`start-${time}-0`} value={time}>
                {time}
              </MenuItem>
            ))}
          </Select>
        </Grid>

        <Grid item md={4} xs={12}>
          <Select
            value={slots[0]?.timeEnd || ""}
            onChange={(e) => handleSlotChange(0, "timeEnd", e.target.value)}
            fullWidth
            size="small"
            sx={{ height: "40px" }}
            displayEmpty
          >
            <MenuItem value="" disabled>
              Select end time
            </MenuItem>
            {ALL_TIME_SLOTS.map((time) => (
              <MenuItem key={`end-${time}-0`} value={time}>
                {time}
              </MenuItem>
            ))}
          </Select>
        </Grid>

        <Grid item md={2} xs={12}>
          <Box sx={{ display: "flex" }}>
            {/* Only show delete on first slot if there are multiple slots */}
            {slots.length > 1 && (
              <Tooltip title="Remove time slot">
                <IconButton
                  onClick={() => removeSlot(0)}
                  size="small"
                  color="error"
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}

            <Tooltip title="Add time slot">
              <IconButton onClick={addSlot} size="small" color="primary">
                <AddIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <Tooltip title="Copy to other days">
              <IconButton
                onClick={handleOpenCopyDialog}
                size="small"
                color="primary"
                disabled={
                  otherDays.length === 0 ||
                  !slots.some((slot) => slot.timeStart && slot.timeEnd)
                }
              >
                <CopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Grid>
      </Grid>

      {/* Additional time slots */}
      {slots.slice(1).map((slot, idx) => {
        const actualIndex = idx + 1;
        return (
          <Grid
            container
            spacing={2}
            alignItems="center"
            key={`${label}-slot-${actualIndex}`}
            sx={{ mb: 1 }}
          >
            {/* Empty space for alignment */}
            <Grid item md={2} xs={12}></Grid>

            <Grid item md={4} xs={12}>
              <Select
                value={slot?.timeStart || ""}
                onChange={(e) =>
                  handleSlotChange(actualIndex, "timeStart", e.target.value)
                }
                fullWidth
                size="small"
                sx={{ height: "40px" }}
                displayEmpty
              >
                <MenuItem value="" disabled>
                  Select start time
                </MenuItem>
                {ALL_TIME_SLOTS.map((time) => (
                  <MenuItem key={`start-${time}-${actualIndex}`} value={time}>
                    {time}
                  </MenuItem>
                ))}
              </Select>
            </Grid>

            <Grid item md={4} xs={12}>
              <Select
                value={slot?.timeEnd || ""}
                onChange={(e) =>
                  handleSlotChange(actualIndex, "timeEnd", e.target.value)
                }
                fullWidth
                size="small"
                sx={{ height: "40px" }}
                displayEmpty
              >
                <MenuItem value="" disabled>
                  Select end time
                </MenuItem>
                {ALL_TIME_SLOTS.map((time) => (
                  <MenuItem key={`end-${time}-${actualIndex}`} value={time}>
                    {time}
                  </MenuItem>
                ))}
              </Select>
            </Grid>

            <Grid item md={2} xs={12}>
              <Box sx={{ display: "flex" }}>
                <Tooltip title="Remove time slot">
                  <IconButton
                    onClick={() => removeSlot(actualIndex)}
                    size="small"
                    color="error"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>

                {/* Only show add button on the last row */}
                {actualIndex === slots.length - 1 && (
                  <Tooltip title="Add time slot">
                    <IconButton onClick={addSlot} size="small" color="primary">
                      <AddIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </Grid>
          </Grid>
        );
      })}

      {/* Copy to Other Days Dialog */}
      <Dialog
        open={copyDialogOpen}
        onClose={handleCloseCopyDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Copy Times From {currentDay}</DialogTitle>
        <DialogContent>
          <Box mb={2}>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Select the days to apply these time slots
            </Typography>
          </Box>
          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  checked={selectAll}
                  onChange={handleSelectAllDays}
                  color="primary"
                />
              }
              label="Select All"
            />
            <Divider sx={{ my: 1 }} />
            {otherDays.map((day) => (
              <FormControlLabel
                key={day}
                control={
                  <Checkbox
                    checked={selectedDays.includes(day)}
                    onChange={() => handleSelectDay(day)}
                    color="primary"
                  />
                }
                label={day}
              />
            ))}
          </FormGroup>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCopyDialog} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleCopyTimeSlots}
            color="primary"
            variant="contained"
            startIcon={<CheckIcon />}
            disabled={selectedDays.length === 0}
          >
            Copy to Selected Days
          </Button>
        </DialogActions>
      </Dialog>

      {/* Feedback Snackbar */}
      <Snackbar
        open={feedback.open}
        autoHideDuration={3000}
        onClose={() => setFeedback({ ...feedback, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Alert
          onClose={() => setFeedback({ ...feedback, open: false })}
          severity={feedback.severity}
          variant="filled"
        >
          {feedback.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TimeSlotComponent;
